import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import BrandMark from "../components/BrandMark";
import { SiteFooter } from "../components/Layout";
import FounderOfTheWeek from "../components/FounderOfTheWeek";
import TraditionHub from "../components/TraditionHub";
import SeoHead from "../components/SeoHead";
import { api } from "../lib/api";
import { Users as UsersIcon, Sparkles, Crown } from "lucide-react";

export default function Landing() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  return (
    <div className="dark-theme min-h-screen relative overflow-hidden">
      <SeoHead
        title="Cauldron — Trusted Spiritual Practitioners, Verified Readings, Sacred Bookings"
        description="The global spiritual marketplace where verified Ifá babalawos, Santería priests, tarot readers, energy healers, mediums, manifestation coaches, and authentic spiritual workers offer online consultations. Online Ifá reading, Orisha consultation, Palo Mayombe consultation, psychic reading online, spiritual cleansing, chakra healing — all trusted, all hand-verified, all escrow-protected."
        keywords="spiritual advisor online, spiritual consultation, psychic reading online, tarot reading online, spiritual healer near me, energy healing online, spiritual mentor, spiritual coach, medium reading online, ancestor reading, love spell caster, relationship spiritual advice, spiritual cleansing service, aura reading online, chakra healing online, spiritual guidance online, trusted spiritual practitioner, authentic spiritual worker, spiritual services marketplace, Ifa consultation, Babalawo near me, online Ifa reading, Orisha consultation, Santeria reading, Santeria priest near me, Lucumi consultation, Isese Ifa consultation, Orunmila reading, Palo Mayombe consultation, Tata Nganga near me, Palo reading, Quimbanda consultation, Exu consultation, Pomba Gira consultation, spiritual awakening, spiritual guidance, spiritual healing, spiritual community, spiritual marketplace, metaphysical services, occult services, divination services, remove negative energy, spiritual protection, spiritual cleansing, bad luck spiritual help, love problems spiritual advice, relationship reading, career tarot reading, court case spiritual help, money manifestation help, ancestor communication, spiritual blockage removal, road opening spiritual work, grow spiritual business, get more spiritual clients, psychic marketing, tarot business growth, spiritual practitioner platform, online marketplace for spiritual services, spiritual worker directory, spiritual practitioner app, verified spiritual practitioners, authentic spiritual services, global spiritual marketplace, find a spiritual practitioner, spiritual booking platform, spiritual community app"
      />
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1653307370781-a5b033bac658?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
      </div>

      <nav className="relative z-10 max-w-[1600px] mx-auto px-8 py-6 flex justify-between items-center">
        <div className="flex items-center gap-3" data-testid="landing-brand">
          <BrandMark size={44} />
          <span className="font-serif text-3xl">cauldron</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/auth?mode=login" className="text-sm tracking-wider uppercase text-foreground/80 hover:text-secondary" data-testid="landing-login-link">Sign In</Link>
          <Link to="/auth?mode=register" className="text-sm tracking-wider uppercase bg-primary text-primary-foreground px-5 py-2 rounded-sm hover:bg-primary/90" data-testid="landing-register-link">Begin</Link>
        </div>
      </nav>

      <main className="relative z-10 max-w-5xl mx-auto px-8 pt-24 pb-12">
        <div className="label-mystic mb-6">A Sacred Marketplace</div>
        <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light leading-[1.05] tracking-tight mb-8">
          Where the spiritual<br/>
          <span className="text-primary italic">commune</span> & <span className="text-secondary italic">commerce</span>.
        </h1>
        <p className="text-lg text-foreground/75 max-w-2xl leading-relaxed mb-12 font-light">
          Cauldron is the first social marketplace for diviners, rootworkers, astrologers,
          ceremonialists, and spiritual practitioners — to broadcast, teach, sell rituals,
          host livestreams, and grow a sacred audience.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/auth?mode=register&role=PROVIDER" className="bg-primary text-primary-foreground px-8 py-4 text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-primary/90" data-testid="landing-cta-provider">
            Become a Practitioner
          </Link>
          <Link to="/auth?mode=register&role=CLIENT" className="border border-secondary text-secondary px-8 py-4 text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-secondary/10" data-testid="landing-cta-client">
            Seek Guidance
          </Link>
          <Link to="/elders" className="text-foreground/80 hover:text-secondary px-3 py-4 text-sm uppercase tracking-[0.2em] inline-flex items-center gap-2" data-testid="landing-cta-elders">
            Meet the 500 Founders →
          </Link>
        </div>

        {/* Live seeker counter — practitioner-facing demand signal */}
        {stats && (
          <div
            className="mt-12 inline-flex flex-wrap items-stretch gap-px rounded-sm border border-secondary/40 bg-secondary/[0.06] overflow-hidden"
            data-testid="landing-public-stats"
            aria-label="Cauldron live platform stats"
          >
            <StatCell
              icon={<UsersIcon size={14} strokeWidth={1.75} />}
              label="Active Seekers"
              value={stats.seekers}
              weekDelta={stats.seekersThisWeek}
              tone="primary"
              testid="landing-stat-seekers"
            />
            <StatCell
              icon={<Crown size={14} strokeWidth={1.75} />}
              label="Verified Practitioners"
              value={stats.practitioners}
              weekDelta={stats.practitionersThisWeek}
              tone="secondary"
              testid="landing-stat-practitioners"
            />
            <StatCell
              icon={<Sparkles size={14} strokeWidth={1.75} />}
              label="Sacred Readings Delivered"
              value={stats.readingsCompleted}
              weekDelta={stats.readingsThisWeek}
              tone="secondary"
              testid="landing-stat-readings"
            />
          </div>
        )}

        {/* Weekly-rotating Founder spotlight */}
        <div className="mt-16 max-w-2xl">
          <FounderOfTheWeek />
        </div>

        {/* Browse by Tradition hub — 6 curated discovery tiles */}
        <TraditionHub />

        <div className="gold-divider my-20" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            ["01", "Subscribe", "Practitioners join for $49.99/mo to unlock the full coven: posts, livestreams, services, payouts."],
            ["02", "Build", "Curate your shrine — services, ratings, livestreams, and a ritual feed your seekers can follow."],
            ["03", "Earn", "Accept bookings, tips, and ad spend. Cauldron takes only 2.30%. The rest is yours."],
          ].map(([n, t, d]) => (
            <div key={n}>
              <div className="font-serif text-5xl text-secondary/70 mb-3">{n}</div>
              <h3 className="font-serif text-2xl mb-3">{t}</h3>
              <p className="text-foreground/70 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        {/* ─── Ambassador CTA — sign up or sign in to the back office ─── */}
        <div className="gold-divider my-20" />
        <section
          className="parchment p-8 sm:p-12 grid md:grid-cols-2 gap-8 items-center"
          data-testid="landing-ambassador-section"
        >
          <div>
            <div className="label-mystic text-secondary">Ambassador Program</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight">
              Get paid to grow the Cauldron.
            </h2>
            <p className="text-foreground/70 text-sm sm:text-base mt-4 leading-relaxed">
              Earn <strong className="text-secondary">$5–$24.99/month</strong> recurring per active
              practitioner subscription plus <strong className="text-secondary">100 CAUL</strong> per
              paid signup. The <strong className="text-secondary">first 20 ambassadors</strong> get
              1 free year of the Practitioner tier — no referrals needed.
            </p>
            <p className="text-foreground/55 text-xs mt-3">
              Instant approval · Access your back office in seconds.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/ambassador/signup"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/85 px-8 py-4 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold inline-flex items-center justify-center gap-2 transition"
              data-testid="landing-ambassador-signup"
            >
              Become an Ambassador
            </Link>
            <Link
              to="/auth?mode=login"
              className="border border-secondary/45 text-secondary/90 hover:bg-secondary/10 px-8 py-4 rounded-sm uppercase tracking-[0.18em] text-xs inline-flex items-center justify-center gap-2 transition"
              data-testid="landing-ambassador-login"
            >
              Ambassador Sign In
            </Link>
            <Link
              to="/ambassadors"
              className="text-center text-xs text-foreground/55 hover:text-secondary uppercase tracking-wider mt-1"
              data-testid="landing-ambassador-learn-more"
            >
              Learn more →
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter withBottomNavOffset={false} />
    </div>
  );
}

function StatCell({ icon, label, value, weekDelta, tone, testid }) {
  const color = tone === "primary" ? "#D95C14" : "#D4B58C";
  return (
    <div className="px-5 py-3 bg-secondary/[0.04]" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1" style={{ color }}>
        {icon}
        <span className="text-[10px] uppercase tracking-[0.22em] opacity-90">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-3xl tabular-nums" style={{ color }}>
          {Number(value || 0).toLocaleString()}
        </span>
        {Number(weekDelta) > 0 && (
          <span
            className="text-[10px] uppercase tracking-[0.18em] opacity-90"
            style={{ color }}
            data-testid={`${testid}-week`}
          >
            +{Number(weekDelta).toLocaleString()} this wk
          </span>
        )}
      </div>
    </div>
  );
}
